import { api, handleApiError } from '../client'
import type { 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse, 
  RefreshTokenRequest 
} from '../types'

interface CurrentUser {
  id: string
  email: string
  fullName: string
  roles: string[]
  permissions: string[]
}

export const authService = {
  async login(data: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/v1/auth/login', data)
      
      // Store tokens
      if (response.data.accessToken && typeof window !== 'undefined') {
        localStorage.setItem('access_token', response.data.accessToken)
        localStorage.setItem('refresh_token', response.data.refreshToken)
        localStorage.setItem('user', JSON.stringify({
          id: response.data.userId,
          email: response.data.email,
          fullName: response.data.fullName,
          roles: response.data.roles,
          permissions: response.data.permissions,
        }))
      }
      
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async register(data: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/v1/auth/register', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async refreshToken(data: RefreshTokenRequest): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/v1/auth/refresh', data)
      
      // Update tokens
      if (response.data.accessToken && typeof window !== 'undefined') {
        localStorage.setItem('access_token', response.data.accessToken)
        localStorage.setItem('refresh_token', response.data.refreshToken)
      }
      
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async logout(): Promise<void> {
    try {
      await api.post('/v1/auth/logout')
    } catch (error) {
      // Continue with local logout even if API call fails
      console.error('Logout API error:', error)
    } finally {
      // Clear local storage
      if (typeof window !== 'undefined') {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
      }
    }
  },

  async forgotPassword(email: string): Promise<void> {
    try {
      await api.post('/v1/auth/forgot-password', { email })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await api.post('/v1/auth/reset-password', { token, newPassword })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async verifyEmail(token: string): Promise<void> {
    try {
      await api.post('/v1/auth/verify-email', { token })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await api.post('/v1/auth/change-password', { currentPassword, newPassword })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Utility methods
  isAuthenticated(): boolean {
    if (typeof window === 'undefined') return false
    return !!localStorage.getItem('access_token')
  },

  getCurrentUser(): CurrentUser | null {
    if (typeof window === 'undefined') return null
    const userStr = localStorage.getItem('user')
    if (!userStr) return null
    try {
      return JSON.parse(userStr) as CurrentUser
    } catch {
      return null
    }
  },

  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser()
    if (!user) return false
    return user.permissions?.includes(permission) || false
  },

  hasRole(role: string): boolean {
    const user = this.getCurrentUser()
    if (!user) return false
    return user.roles?.includes(role) || false
  },

  hasAnyRole(roles: string[]): boolean {
    const user = this.getCurrentUser()
    if (!user || !user.roles) return false
    return roles.some(role => user.roles.includes(role))
  },

  hasAllRoles(roles: string[]): boolean {
    const user = this.getCurrentUser()
    if (!user || !user.roles) return false
    return roles.every(role => user.roles.includes(role))
  },
}